<template>
    <div class="checkout_product_row-container">
        <div class="checkout_product_row">
            <DeletedListItemUndo
                v-if="pendingUndo"
                :item-name="pendingUndo.itemName"
                @undo="undoDelete"
            />
            <ListOrderItem
                v-for="(listItem, index) in listItems"
                :key="listItem.list_item_id"
                v-model:listItem="listItems[index]"
                @openItemDeleteModal="openItemDeleteModal($event)"
                :disable-edit-quantity="props.readonly"
                :disable-change-item="props.readonly"
                :disable-delete-item="props.readonly"
                :show-quantity-in-details="props.readonly"
                :is-restricted="isRestricted(listItem)"
            />
            <ListOrderItem
                v-for="(couponItem, index) in couponItems"
                :key="index"
                class="free-item"
                v-model:listItem="couponItems[index]"
                :disable-edit-quantity="true"
                :disable-change-item="false"
                :disable-delete-item="true"
                :show-quantity-in-details="props.readonly"
                :free-item="couponItem.price<=0"
            />
        </div>
        <div class="checkout_product_row-show_more" v-if="listItems.length > 3">
            <input v-model="showingMoreItems" id="show_more_checkout_products" class="checkout_product_row-show_more-input" type="checkbox" aria-label="Show more checkout products" />
            <div class="checkout_product_row-line"></div>
            <label @click="toggleShowingMoreItems()" for="show_more_checkout_products">
                <a>Show {{ showingMoreItems ? 'Less Items' : `All ${listItems.length } Items` }}</a>
            </label>
            <div class="checkout_product_row-line"></div>
        </div>
    </div>
</template>
<script lang="ts" setup>
    import { ref, computed, inject, onUnmounted } from "vue";
    import ListOrderItem from "./cart/ListOrderItem.vue";
    import DeletedListItemUndo from "./cart/DeletedListItemUndo.vue";
    import ListDeleteForm from "@/components/ListDeleteForm.vue";
    import { useDialog } from "primevue/usedialog";
    import { useCheckoutD2CStore } from '@/store/checkoutD2CStore';
    import { useLists } from "@/helpers/composables/useList";
    import { router } from "@inertiajs/vue3";
    import { storeToRefs } from "pinia";
    import {
        getPseudoQtyForItem,
        quantityForBackendWithPseudoLine,
    } from "@/helpers/composables/useCouponPseudoLine";

    let route = inject('route');

    const UNDO_TIMEOUT_MS = 10000;

    let listItems = defineModel("listItems", {
        type: Array<Object>,
        default: []
    });

    let props = defineProps({
        currentList: {
            type: Object,
            default() {
                return {};
            }
        },
        restrictedItems: {
            type: Array<Object>,
            default: []
        },
        couponItems: {
            type: Array<Object>,
            default: []
        },
        readonly: {
            type: Boolean,
            default: false
        },
    });

    let dialog = useDialog(); 

    const {
        recalculateTax, getShippingRates, reloadOrderInfo, reapplyCoupon
    } = useCheckoutD2CStore();

    const { isUpdatingCartItem, couponInformation } = storeToRefs(useCheckoutD2CStore());
    const { addToList } = useLists({ router, route });

    let deleteOpen = ref(false);
    let currentType = ref("item");
    let currentLID = ref();
    const showingMoreItems = ref(false);
    const toggleShowingMoreItems = ()=>(showingMoreItems.value = !showingMoreItems.value);

    const pendingUndo = ref(null);
    let undoTimeout = null;
    let deleteSnapshot = null;

    const clearUndoTimeout = () => {
        if (undoTimeout) {
            clearTimeout(undoTimeout);
            undoTimeout = null;
        }
    };

    const showPendingUndo = (snapshot) => {
        clearUndoTimeout();
        pendingUndo.value = snapshot;
        undoTimeout = setTimeout(() => {
            pendingUndo.value = null;
            undoTimeout = null;
        }, UNDO_TIMEOUT_MS);
    };

    const createDeleteSnapshot = (listItem) => {
        const rawListItem = listItems.value.find(
            (item) => item.list_item_id === listItem.list_item_id
        );

        if (!rawListItem) {
            return null;
        }

        const pseudoQty = getPseudoQtyForItem(
            couponInformation.value?.items,
            rawListItem.item_variation_id
        );

        return {
            item_variation_id: rawListItem.item_variation_id,
            quantity: quantityForBackendWithPseudoLine(rawListItem.quantity, pseudoQty),
            list_id: props.currentList.list_id,
            source: rawListItem.source,
            itemName: rawListItem.variation.item.name,
        };
    };

    const beforeCloseForItemFunc = ()=>{
        reapplyCoupon();
        recalculateTax();
        getShippingRates();
        reloadOrderInfo();
    };

    const handleItemDeleted = () => {
        if (!props.readonly && deleteSnapshot) {
            showPendingUndo(deleteSnapshot);
        }

        deleteSnapshot = null;
        beforeCloseForItemFunc();
    };

    let openItemDeleteModal = function ($event: Event) {
        const listItem = $event.value;
        deleteSnapshot = createDeleteSnapshot(listItem);

        currentLID.value = listItem.list_item_id;
        deleteOpen.value = true;
        dialog.open(ListDeleteForm, {
            props: {
                modal: true,
                header: "Delete List Item",
            },
            data: {
                list_id: props.currentList.list_id,
                list_item_id: listItem.list_item_id,
                type: currentType.value,
                after_delete:afterDelete,
                beforeCloseForItem: handleItemDeleted
            }
        })
    };

    const undoDelete = () => {
        if (!pendingUndo.value) {
            return;
        }

        const snapshot = pendingUndo.value;
        clearUndoTimeout();
        pendingUndo.value = null;
        isUpdatingCartItem.value = true;

        addToList(
            {
                item_variation_id: snapshot.item_variation_id,
                quantity: snapshot.quantity,
                listId: snapshot.list_id,
                source: snapshot.source,
            },
            {
                afterFetch: () => {
                    isUpdatingCartItem.value = false;
                    beforeCloseForItemFunc();
                },
            }
        );
    };

    const isRestricted = (listItem) => {
        return props.restrictedItems.some(
            restrictedItem => restrictedItem.list_item_id === listItem.list_item_id
      );
    }

    const couponItems = computed(() => {
        return props.couponItems.map(item => ({
            ...item,
            list_id: props.currentList.list_id,
            unit_price: item.priceWithoutDiscount,
            price: item.discountPrice
        }));
    });

    const afterDelete = computed(() => {
        return route("shop.show");
    });

    onUnmounted(() => {
        clearUndoTimeout();
    });
</script>
